import { LoadingSmall } from "@/components/LoadingSmall";
import { Button } from "@/components/ui/button";
import { showSuccess, showApiError } from "@/lib/toast";
import { publishEntity } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PermissionCheckerStandaloneEndpoint } from "@/components/permission/PermissionChecker2";
import { ConfirmActionDialog, ConfirmActionDialogRef } from "@/components/ConfirmActionDialog";
import { useRef } from "react";

interface Props {
  channelGuid: string;
}

export const ChannelPublishButton: React.FC<Props> = ({ channelGuid }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const dialogRef = useRef<ConfirmActionDialogRef>(null);

  const queryClient = useQueryClient();
  const publishMutation = useMutation({
    mutationFn: () => publishEntity(String(activeFirm?.firmGuid), String(channelGuid), "ChannelGuid"),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getChannelDetails"],
      });
      showSuccess(t("contentGroup.publishedSuccess"));
    },
    onError: showApiError,
  });

  const publishButton = (
    <Button className="bg-green-500 hover:bg-green-600" disabled={publishMutation.isPending} size="sm">
      {t("contentGroup.publish")}
      {publishMutation.isPending && <LoadingSmall />}
    </Button>
  );

  return (
    <PermissionCheckerStandaloneEndpoint apiEndpoint={`v1/Publish`}>
      <ConfirmActionDialog
        ref={dialogRef}
        title={t("contentGroup.publishConfirmTitle")}
        description={t("contentGroup.publishConfirmDesc")}
        confirmButtonText={t("contentGroup.publish")}
        trigger={publishButton}
        onConfirm={async () => {
          await publishMutation.mutateAsync();
          dialogRef.current?.close();
        }}
        isLoading={publishMutation.isPending}
      />
    </PermissionCheckerStandaloneEndpoint>
  );
};
